import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { LogOut, Wallet as WalletIcon, Sparkles, Menu, X, Leaf } from "lucide-react";
import { buildAvatarUrl, defaultAvatar } from "@/lib/avatar";

const LINKS = [
  { to: "/", label: "Ecosystem", testid: "nav-ecosystem" },
  { to: "/wallet", label: "Wallet", testid: "nav-wallet" },
];

const MOBILE_EXTRA = [
  { to: "/profile", label: "Profile", testid: "nav-m-profile" },
];

const GAME4YOU_ROUTES = ["/welcome", "/games", "/game", "/limbo", "/tower", "/crews", "/crew", "/membership", "/leaderboard", "/profile", "/shop", "/entertainment", "/matches", "/login"];

export default function Nav() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  if (loc.pathname === "/login") return null;

  const isGame4You = GAME4YOU_ROUTES.some((p) => loc.pathname === p || loc.pathname.startsWith(p + "/"));

  const isActive = (p) => loc.pathname === p || loc.pathname.startsWith(p + "/");
  const onLogout = async () => {
    await logout();
    nav("/");
  };

  const avatarUrl = user ? buildAvatarUrl(user.avatar || defaultAvatar(user.user_id), 120) : null;

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-ink-800/70 border-b border-white/10" data-testid="main-nav">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <button className="md:hidden p-2 -ml-2 text-white/70 hover:text-white" onClick={() => setOpen(!open)} data-testid="mobile-menu-button" aria-label="Menu">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-home-link">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-brand-magenta to-brand-amber grid place-items-center shadow-glow-magenta">
              {isGame4You ? <Sparkles className="w-4 h-4 text-white" strokeWidth={2.5} /> : <Leaf className="w-4 h-4 text-white" strokeWidth={2.5} />}
            </div>
            {isGame4You ? (
              <span className="font-display font-extrabold text-lg tracking-tight" data-testid="logo-game4you">
                GAME<span className="gradient-text">4YOU</span>
              </span>
            ) : (
              <span className="font-display font-extrabold text-lg tracking-tight" data-testid="logo-wizlife">
                WIZ<span className="gradient-text">LIFE</span>
              </span>
            )}
          </Link>
        </div>

        <nav className="hidden md:flex items-center gap-1">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} active={isActive(l.to)} testid={l.testid}>{l.label}</NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 shrink-0">
          {user ? (
            <>
              <Link
                to="/wallet"
                className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:border-brand-cyan/50 transition-colors"
                data-testid="nav-user-pill"
              >
                <WalletIcon className="w-3.5 h-3.5 text-brand-cyan" />
                <span className="font-mono text-xs text-white">{user.name}</span>
              </Link>
              <Link to="/profile" className="w-9 h-9 rounded-full overflow-hidden border border-white/15 hover:border-brand-magenta transition-colors" data-testid="nav-avatar">
                <img src={avatarUrl} alt={user.name} className="w-full h-full object-cover" />
              </Link>
              <button onClick={onLogout} className="btn-ghost !p-2" data-testid="logout-button" aria-label="Log out">
                <LogOut className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <Link to="/register" className="btn-ghost !py-2 !px-4 text-sm" data-testid="nav-register-cta">
                Register
              </Link>
              <Link to="/login" className="btn-primary !py-2 !px-5 text-sm" data-testid="nav-login-cta">
                Login
              </Link>
            </>
          )}
        </div>
      </div>

      {open && (
        <nav className="md:hidden border-t border-white/10 bg-ink-800/95 backdrop-blur-xl px-4 py-3 grid grid-cols-2 gap-1" data-testid="mobile-menu">
          {[...LINKS, ...MOBILE_EXTRA].map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              data-testid={l.testid + "-mobile"}
              className={`px-3 py-2.5 font-display font-semibold text-sm rounded-lg ${isActive(l.to) ? "text-white bg-white/10" : "text-white/65"}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>
      )}
    </header>
  );
}

function NavLink({ to, active, children, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`px-3.5 py-2 font-display font-semibold text-sm rounded-full transition-all ${
        active
          ? "text-white bg-white/10"
          : "text-white/65 hover:text-white hover:bg-white/5"
      }`}
    >
      {children}
    </Link>
  );
}
